import { animate, style, transition, trigger } from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import '@angular/localize/init';
import { MatCalendar } from '@angular/material/datepicker';
import { MatOptionSelectionChange } from '@angular/material/core';
import { Router } from '@angular/router';
import {
  Calendar,
  DateRangeType,
  MeetingLocationType,
  HostUserInterface,
  EventTypeDateRangeInterface,
} from '@vendasta/meetings';
import { format } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { Observable, Subscription } from 'rxjs';
import { distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { BookMeNowStoreService } from '../book-me-now-store.service';
import { TimeSlot } from '../bookme/bookme';
import { NavigationService } from '../navigation.service';
import { buildTimezoneDisplay, getTimezoneNames } from '../util';
import { MeetingInfo } from './meeting-info/meeting-info';

function localDayKey(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

function zonedDayKey(date: Date, timezone: string): string {
  return formatInTimeZone(date, timezone, 'yyyy-MM-dd');
}

export function areDayKeysEqual(x: string, y: string): boolean {
  return x === y;
}

export function isTimeSlotLaterThanNow(timeSlot: Date): boolean {
  const today = new Date();
  return timeSlot > today;
}

export interface TimezoneOption {
  timezone: string;
  display: string;
}

@Component({
  selector: 'app-bookme-calendar',
  templateUrl: './bookme-calendar.component.html',
  styleUrls: ['./bookme-calendar.component.scss'],
  animations: [
    trigger('slideInOut', [
      transition('void => *', [
        style({ display: 'block', opacity: 0, transform: 'translateX(100%)' }),
        animate('300ms ease-in-out'),
      ]),
      transition('* => void', [
        animate(
          '300ms ease-in-out',
          style({ opacity: 0, transform: 'translateX(100%)' })
        ),
      ]),
    ]),
    trigger('fadeSwapOut', [
      transition('* => void', [
        animate(
          '300ms ease-in-out',
          style({ opacity: 0, transform: 'scale(0.8) translateX(100%)' })
        ),
      ]),
    ]),
  ],
  standalone: false,
})
export class BookmeCalendarComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  @Input() set timeSlots(value: TimeSlot[] | Error) {
    this._timeSlots =
      value && Array.isArray(value)
        ? value.filter((timeSlot) => isTimeSlotLaterThanNow(timeSlot.start))
        : null;
    if (this.displayCalendar && this.displayCalendar.monthView) {
      this.displayCalendar.monthView._init();
    }
  }

  get timeSlots(): TimeSlot[] | Error {
    return this._timeSlots;
  }

  set dateSelected(value: Date) {
    this._dateSelected = value;
    this.selectedTimeSlot = null;
    this.bookmeNowStoreService.setSelectedDate(value);
    this.timeSlotsForDateSelected =
      value && Array.isArray(this.timeSlots)
        ? this.timeSlots.filter((timeSlot) =>
            areDayKeysEqual(
              localDayKey(value),
              zonedDayKey(timeSlot.start, this.timezone)
            )
          )
        : null;
    this.cd.markForCheck();
  }

  get dateSelected(): Date {
    return this._dateSelected;
  }

  get maxDate(): Date | null {
    if (this.isCustomDateRange()) {
      return this.dateRange.customDateRange.end;
    }
    return null;
  }

  get minDate(): Date {
    if (this.isCustomDateRange()) {
      return this.dateRange.customDateRange.start;
    }
    return this.now;
  }

  get currentView():
    | 'select-a-date'
    | 'no-available-dates'
    | 'select-a-time'
    | 'loading'
    | 'error'
    | 'select-a-host' {
    if (this.dateSelected) {
      return 'select-a-time';
    }

    if (Array.isArray(this.timeSlots)) {
      if (this.timeSlots.length > 0) {
        return 'select-a-date';
      }
      return 'no-available-dates';
    }
    if (this.loadTimeSlotsError instanceof Error) {
      return 'error';
    }
    return 'loading';
  }

  constructor(
    private readonly cd: ChangeDetectorRef,
    private readonly bookmeNowStoreService: BookMeNowStoreService,
    public navigationService: NavigationService,
    private readonly router: Router
  ) {}
  private _timeSlots: TimeSlot[] | Error = null;
  private _dateSelected: Date = null;
  private allTimezoneOptions: TimezoneOption[];

  private timezone: string;
  timezone$: Observable<string>;
  timezoneOffset$: Observable<string>;

  timezoneOptions$: Observable<TimezoneOption[]>;
  timezoneControl: UntypedFormControl;
  location$: Observable<string>;
  selectedTimeSlot: number;
  newLocation: string;
  locationGuideline: string;
  isClientLocation: boolean;
  teamMemberList$: Observable<HostUserInterface[]>;
  isClientChoiceMeeting$: Observable<boolean>;
  isTimeSlotLoading$: Observable<boolean>;
  currentPath: string;
  dateRange: EventTypeDateRangeInterface;

  selectedHost: string;

  get isConfirmButtonDisabled(): boolean {
    // Time zone needs to be selected
    if (!this.timezone) {
      return true;
    }
    // Allow modification of only location if its client choice
    // If timezone is modified then date & time needs to be selected
    if (this.isClientLocation && this.displayLocationEdit) {
      // location cannot be empty
      if (this.newLocation.trim() === '') {
        return true;
      }
      // Ignore check if timezone is modified
      if (!this.bookmeNowStoreService.getTimezoneChanged()) {
        // If date is modified time needs to be selected
        if (
          (this.selectedTimeSlot === null ||
            this.selectedTimeSlot === undefined) &&
          this.dateSelected
        ) {
          return true;
        }
        if (this.bookmeNowStoreService.isLocationModified(this.newLocation)) {
          return false;
        }
      }
    }
    return (
      this.selectedTimeSlot === null || this.selectedTimeSlot === undefined
    );
  }

  @Input() calendar: Calendar;
  @Input() rescheduling = false;
  @Input() rescheduledMeetingInfo: MeetingInfo;
  @Input() loadTimeSlotsError: Error;
  @Input() displayLocationEdit = false;

  @Output() timeRangeChanged = new EventEmitter<{ start: Date; end: Date }>();
  @Output() timeSlotSelected = new EventEmitter<TimeSlot>();
  @Output() timezoneChanged = new EventEmitter<string>();
  @Output() locationChanged = new EventEmitter<string>();
  @Output() confirmChanges = new EventEmitter<void>();
  @Output() hostChanged = new EventEmitter<string>();

  timeSlotsForDateSelected: TimeSlot[] = null;
  now = new Date();

  @ViewChild('displayCalendar') private displayCalendar: MatCalendar<Date>;

  private readonly subscriptions: Subscription[] = [];

  protected readonly location = location;

  dateFilter = (d: Date): boolean => {
    if (Array.isArray(this.timeSlots)) {
      return !!this.timeSlots.find((timeSlot) =>
        areDayKeysEqual(localDayKey(d), zonedDayKey(timeSlot.start, this.timezone))
      );
    }
    return false;
  }; // tslint:disable-line:semicolon

  ngOnInit(): void {
    this.currentPath = this.router.url;

    // Initialize timezone control and options first
    const timezones = getTimezoneNames();
    const currentTimezoneDisplay = this.buildTimezoneDisplay(this.timezone);
    this.timezoneControl = new UntypedFormControl(currentTimezoneDisplay);
    this.allTimezoneOptions = timezones.map((tz) => {
      return { display: this.buildTimezoneDisplay(tz), timezone: tz };
    });
    this.timezoneOptions$ = this.timezoneControl.valueChanges.pipe(
      startWith(currentTimezoneDisplay),
      map((searchValue) => {
        if (!searchValue || searchValue.trim() === '') {
          return this.allTimezoneOptions;
        }

        const searchTerm = searchValue.toLowerCase().trim();
        return this.allTimezoneOptions.filter((option) => {
          const displayLower = option.display.toLowerCase();
          const timezoneLower = option.timezone.toLowerCase();

          // Search in both display format and timezone name
          return (
            displayLower.includes(searchTerm) ||
            timezoneLower.includes(searchTerm)
          );
        });
      })
    );

    // Now set up timezone subscriptions
    this.timezone$ = this.bookmeNowStoreService.timezone$;
    this.timezoneOffset$ = this.bookmeNowStoreService.timezoneOffset$;
    this.subscriptions.push(
      this.timezone$.subscribe((timezone) => {
        this.timezone = timezone;
        // Update the existing control instead of creating a new one
        if (this.timezoneControl) {
          this.timezoneControl.setValue(this.buildTimezoneDisplay(timezone));
        }
      })
    );

    this.location$ = this.bookmeNowStoreService.newMeetingLocation$;
    this.location$.subscribe((location) => {
      this.newLocation = location;
    });

    this.bookmeNowStoreService.locationGuideLine$.subscribe(
      (locationGuideLine) => {
        this.locationGuideline = locationGuideLine;
      }
    );

    this.subscriptions.push(
      this.bookmeNowStoreService.dateRange$.subscribe((dateRange) => {
        this.dateRange = dateRange;
        // Update calendar if dateRange changes and component is initialized
        if (this.displayCalendar && this.isCustomDateRange()) {
          this.displayCalendar.activeDate =
            this.dateRange.customDateRange.start;
        }
      })
    );
    this.bookmeNowStoreService.locationType$.subscribe((locationType) => {
      this.isClientLocation =
        locationType === MeetingLocationType.IN_PERSON_CLIENT_SITE;
    });

    this.teamMemberList$ = this.getConnectedHostUsers();
    this.isClientChoiceMeeting$ =
      this.bookmeNowStoreService.isClientChoiceMeeting$;
    this.isTimeSlotLoading$ = this.bookmeNowStoreService.isLoadingTimeSlots$;
  }

  ngAfterViewInit(): void {
    if (this.isCustomDateRange()) {
      this.displayCalendar.activeDate = this.dateRange.customDateRange.start;
    }

    let lastMonth = this.displayCalendar.activeDate?.getMonth();

    this.subscriptions.push(
      this.displayCalendar.stateChanges
        .pipe(
          startWith(1),
          map(() => this.displayCalendar.activeDate),
          distinctUntilChanged((prev, curr) =>
            areDayKeysEqual(localDayKey(prev), zonedDayKey(curr, this.timezone))
          )
        )
        .subscribe((activeDate) => {
          const currentMonth = activeDate.getMonth();

          if (currentMonth !== lastMonth) {
            lastMonth = currentMonth;
            this.timeSlots = null;
            const timeRange = this.buildTimeRangeFromActiveDate(activeDate);
            this.timeRangeChanged.emit(timeRange);
            this.cd.detectChanges();
          }
        })
    );

    // Additional subscription for timezone changes
    this.subscriptions.push(
      this.timezone$.pipe(distinctUntilChanged()).subscribe((newTimezone) => {
        this.timezone = newTimezone;
        this.timeRangeChanged.emit(
          this.buildTimeRangeFromActiveDate(this.displayCalendar.activeDate)
        );
      })
    );
  }

  private buildTimeRangeFromActiveDate(ad: Date): { start: Date; end: Date } {
    return {
      start: new Date(ad.getFullYear(), ad.getMonth(), 1),
      end: new Date(ad.getFullYear(), ad.getMonth() + 1, 1),
    };
  }

  getConnectedHostUsers(): Observable<HostUserInterface[]> {
    return this.bookmeNowStoreService.teamMemberList$.pipe(
      map((hostUsers) => {
        if (!hostUsers || !Array.isArray(hostUsers)) {
          return [];
        }

        // Check if any users are connected
        const hasConnectedUsers = hostUsers.some((user) => user.isConnected);

        // If there are connected users, return only connected users
        // If no users are connected, return all users
        return hasConnectedUsers
          ? hostUsers.filter((user) => user.isConnected)
          : hostUsers;
      })
    );
  }

  onConfirmChanges(): void {
    if (this.isClientLocation && this.displayLocationEdit) {
      this.locationChanged.emit(this.newLocation);
    }
    this.confirmChanges.emit();
  }

  onLocationChanged(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.bookmeNowStoreService.setNewMeetingLocation(target.value);
  }

  onTimeSlotSelected(index: number, timeSlot: TimeSlot): void {
    this.selectedTimeSlot = index;
    this.timeSlotSelected.emit(timeSlot);
  }

  clearInput() {
    this.timezone = '';
    this.selectedTimeSlot = null;
    this.dateSelected = null;
    this.timezoneControl.setValue(null);
    this.timezoneChanged.emit(null);
  }

  onTimeZoneChanged(timezoneChange: MatOptionSelectionChange): void {
    if (timezoneChange.isUserInput) {
      this.timezone = timezoneChange.source.value;
      const currentTimezoneDisplay = this.buildTimezoneDisplay(this.timezone);
      this.timezoneControl.setValue(currentTimezoneDisplay);
      this.timezoneChanged.emit(this.timezone);
      this.selectedTimeSlot = null;
      this.dateSelected = null;
    }
  }

  timeOptionTrack(i: number, timezoneOption: TimezoneOption): string {
    return timezoneOption.timezone;
  }

  timeSlotTrackBy(i: number, timeSlot: TimeSlot): string {
    return `${i}-${timeSlot.start.getTime()}-${timeSlot.end.getTime()}`;
  }

  private buildTimezoneDisplay(timezone: string): string {
    return buildTimezoneDisplay(timezone);
  }

  changeSelectedHost(selectedHostUserId) {
    this._dateSelected = null;
    this.selectedHost = selectedHostUserId.value;
    this.hostChanged.emit(this.selectedHost);
  }

  isCustomDateRange() {
    if (
      this.dateRange &&
      this.dateRange.dateRangeType === DateRangeType.CUSTOM &&
      this.dateRange.customDateRange
    ) {
      return true;
    }
    return false;
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }
}
